import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import styles from './Modal.module.css';

export type ModalSize = 'sm' | 'md' | 'lg';

/** `side` slides a full-height panel in from the right edge instead of centring a dialog. */
export type ModalPlacement = 'center' | 'side';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string | undefined;
  footer?: ReactNode | undefined;
  size?: ModalSize | undefined;
  /** Set to `false` for destructive confirmations that must be answered explicitly. */
  closeOnOverlayClick?: boolean | undefined;
  closeLabel?: string | undefined;
  placement?: ModalPlacement | undefined;
  /** Optional: a confirmation can be all title, description and footer. */
  children?: ReactNode;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Rendered in a portal on `document.body` so an ancestor's `overflow` or `transform` cannot clip
 * or re-anchor the dialog. Focus is trapped by hand rather than pulling in a dialog library: the
 * bundle budget (RNF04) does not justify the dependency for one component.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
  closeLabel = 'Fechar',
  placement = 'center',
  children,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const generatedId = useId();
  const titleId = `modal-title-${generatedId}`;
  const descriptionId = `modal-description-${generatedId}`;

  const focusableElements = useCallback((): HTMLElement[] => {
    const root = dialogRef.current;
    if (root === null) {
      return [];
    }
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (element) => element.offsetParent !== null || element === document.activeElement,
    );
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    previouslyFocusedRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const [first] = focusableElements();
    (first ?? dialogRef.current)?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocusedRef.current?.focus();
    };
  }, [open, focusableElements]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const elements = focusableElements();
      const first = elements[0];
      const last = elements[elements.length - 1];

      if (first === undefined || last === undefined) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }

      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === dialogRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [open, onClose, focusableElements]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      className={[styles.overlay, placement === 'side' ? styles.overlaySide : null]
        .filter(Boolean)
        .join(' ')}
    >
      {closeOnOverlayClick ? (
        // A real button rather than a click handler on the overlay: the dismiss affordance has to
        // be reachable by keyboard, and it is the last stop in the trap so Tab cycles back.
        <button
          type="button"
          className={styles.backdrop}
          onClick={onClose}
          aria-label={closeLabel}
          tabIndex={-1}
        />
      ) : null}
      <div
        ref={dialogRef}
        className={[styles.dialog, placement === 'side' ? styles.side : styles[size]].join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description !== undefined ? descriptionId : undefined}
        tabIndex={-1}
      >
        <header className={styles.header}>
          <div>
            <h2 className={styles.title} id={titleId}>
              {title}
            </h2>
            {description !== undefined ? (
              <p className={styles.description} id={descriptionId}>
                {description}
              </p>
            ) : null}
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label={closeLabel}>
            <span aria-hidden="true">×</span>
          </button>
        </header>

        {children !== undefined && children !== null ? (
          <div className={styles.body}>{children}</div>
        ) : null}

        {footer !== undefined ? <footer className={styles.footer}>{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
